import { CreateOrUpdateMetrics_Request } from "build-ts/interface";
import showToast from "../hooks/useCustomToast";
import { Header, MetricProto } from "build-ts/metadata";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";

/**
 * Fetches a list of all metrics.
 *
 * @returns {Promise<any>} A promise that resolves to the list of metrics.
 * @throws {Error} Throws an error if the request fails.
 */
export const listMetrics = async (modelId: string): Promise<any> => {
  try {
    const response = await fetch(`${process.env.REACT_APP_LIST_METRIC!}?modelId=${modelId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Failed to fetch data", "", "error");
      return null;
    }
    const data = await response.json();
    return data.map((header: any) => Header.fromJSON(header));
  } catch (error) {
    showToast("Failed to fetch Metrics list", "", "error");
  }
};

/**
 * Creates a new metric.
 *
 * @param {CreateUpdateMetric} formData - The form data for creating the metric.
 * @returns {Promise<any>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const createMetric = async (formData: CreateOrUpdateMetrics_Request) => {
  try {
    const response = await fetch(process.env.REACT_APP_CREATE_METRIC!, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(CreateOrUpdateMetrics_Request.toJSON(formData)),
    });
    
    if (!response.ok) {
      showToast("Error in creating metric", "", "error");
      return null;
    }
    const data = await response.json();
    const result = MetricProto.fromJSON(data.metrics[0]);
    return result;
  } catch (error) {
    showToast("Error in creating metric", "", "error");
  }
};

/**
 * Creates a new metric.
 *
 * @param {CreateUpdateMetric} formData - The form data for creating the metric.
 * @returns {Promise<any>} A promise that resolves to the API response.
 * @throws {Error} Throws an error if the request fails.
 */
export const updateMetric = async (formData: CreateOrUpdateMetrics_Request) => {
  try {
    if (!process.env.REACT_APP_UPDATE_METRIC) {
      throw new Error("Could not create metric");
    }

    const response = await fetch(process.env.REACT_APP_UPDATE_METRIC, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(CreateOrUpdateMetrics_Request.toJSON(formData)),
    });

    if (!response.ok) {
      showToast("Error updating metric", "", "error");
      return null;
    }
    const data = await response.json();
    return MetricProto.fromJSON(data);
  } catch (error) {
    showToast(`Error creating metric: ${error}`, "", "error");
  }
};

/**
 * Fetches metric information based on the provided metric ID.
 *
 * @param {string} id - The ID of the metric to fetch.
 * @returns {Promise<any>} A promise that resolves to the metric information.
 * @throws {Error} Throws an error if the request fails.
 */
export async function getMetricById(id: string, condition: boolean) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_METRIC_BY_ID}/${id}?includeEdges=${condition}`,
      {
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Network response was not ok", "", "error");
      return null;
    }

    const data = await response.json();
    return MetricProto.fromJSON(data);
  } catch (error) {
    showToast("Error fetching metric", "", "error");
  }
}

/**
 * Fetches metrics knowledge graphs for given metric IDs based on the includeEdges flag.
 * 
 * @param {string[]} metricIds - The collection of metric IDs to fetch.
 * @param {boolean} includeEdges - Whether to include metric edges in the result.
 * @returns {Promise<MetricKnowledgeGraph[]>} A promise that resolves to the metrics knowledge graphs.
 * @throws {Error} Throws an error if the request fails.
 */
export async function getAllMetrics(metricIds: string[], includeEdges: boolean): Promise<MetricProto[]> {
  try {
    const params = new URLSearchParams();
    params.append("metricIds", JSON.stringify(metricIds));
    params.append("includeEdges", includeEdges.toString());
    const response = await fetch(`${process.env.REACT_APP_GET_ALL_METRICS!}?${params.toString()}`, {
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Network response was not ok", "", "error");
      return [];
    }
    
    const data = await response.json();
    return data.map((metric: any) => MetricProto.fromJSON(metric));
  } catch (error) {
    showToast("Error fetching metrics and their knowledge graphs", "", "error");
    return [];
  }
};

/**
 * delete metric by ID.
 *
 * @param {string} id - The ID of the metric to delete.
 * @returns {Promise<any>} A promise that resolves to the metric deletion.
 * @throws {Error} Throws an error if the request fails.
 */
export async function deleteMetric(id: string) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_DELETE_METRIC}/${id}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (response.ok) {
      // Success - no content to return
      return null;
    }

    if (response.status === 409) {
      // Conflict - there are dependencies, return the JSON response
      const responseJson = await response.json();
      return responseJson;
    }

    // Any other status is an error
    showToast("Error deleting metric", "", "error");
    return null;
  } catch (error) {
    showToast("Error deleting metric", "", "error");
  }
};

/**
 * Fetches the list of all benchmarks.
 * 
 * @returns {Promise<Header[]>} A promise that resolves to the list of all benchmarks.
 * @throws {Error} Throws an error if the request fails.
 */
export const getAllBenchmarks = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_GET_ALL_BENCHMARKS!}`, {
      credentials: "include",
    });

    if (!response.ok) {
      showToast("Error fetching all benchmarks", "", "error");
      return null;
    }

    const data = await response.json(); 
    return data.map((header: any) => Header.fromJSON(header));;
  } catch (error) {
    showToast("Error fetching all benchmarks", "", "error");
  }
};

/**
 * Saves a benchmark.
 *
 * @param {BenchmarkEntityProto} benchmark - The benchmark to save.
 * @returns {Promise<BenchmarkEntityProto>} A promise that resolves to the saved benchmark.
 * @throws {Error} Throws an error if the request fails.
 */
export const saveBenchmark = async (benchmark: BenchmarkEntityProto) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_PUT_BENCHMARK!}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(BenchmarkEntityProto.toJSON(benchmark)),
    });

    if (!response.ok) {
      showToast("Error saving benchmark", "", "error");
      return null;
    }

    const data = await response.json(); 
    return BenchmarkEntityProto.fromJSON(data);
  } catch (error) {
    showToast("Error saving benchmark", "", "error");
  }
};

/**
 * Fetches benchmark information based on the provided benchmark ID.
 *
 * @param {string} id - The ID of the benchmark to fetch.
 * @returns {Promise<BenchmarkEntityProto>} A promise that resolves to the benchmark information.
 * @throws {Error} Throws an error if the request fails.
 */
export async function getBenchmarkById(id: string, condition: boolean) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_GET_BENCHMARK_BY_ID}/${id}`,{
        method: "GET",
        credentials: "include",
      }
    );

    if (!response.ok) {
      showToast("Network response was not ok", "", "error");
      return null;
    }

    const data = await response.json();
    return BenchmarkEntityProto.fromJSON(data);
  } catch (error) {
    showToast("Error fetching benchmark", "", "error");
  }
};

/**
 * delete benchmark by ID.
 *
 * @param {string} id - The ID of the benchmark to delete.
 * @returns {Promise<any>} A promise that resolves to the benchmark deletion.
 * @throws {Error} Throws an error if the request fails.
 */
export async function deleteBenchmark(id: string) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_DELETE_BENCHMARK}/${id}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (response.ok) {
      // Success - no content to return
      return null;
    }

    if (response.status === 409) {
      // Conflict - there are dependencies, return the JSON response
      const responseJson = await response.json();
      return responseJson;
    }

    // Any other status is an error
    showToast("Error deleting benchmark", "", "error");
    return null;
  } catch (error) {
    showToast("Error deleting benchmark", "", "error");
  }
};

/**
 * delete benchmark by ID.
 *
 * @param {string} id - The ID of the benchmark to delete.
 * @returns {Promise<any>} A promise that resolves to the benchmark deletion.
 * @throws {Error} Throws an error if the request fails.
 */
export async function deleteKgColumn(id: string) {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_DELETE_KG_COLUMN}/${id}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (response.ok) {
      // Success - no content to return
      return null;
    }

    if (response.status === 409) {
      // Conflict - there are dependencies, return the JSON response
      const responseJson = await response.json();
      return responseJson;
    }

    // Any other status is an error
    showToast("Error deleting kg column", "", "error");
    return null;
  } catch (error) {
    showToast("Error deleting kg column", "", "error");
  }
};